import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X, RefreshCw, ArrowRightLeft, TrendingUp, PiggyBank } from 'lucide-react';
import { TraspasoRecurrente } from '../types';
import { TIPO_INVERSION_ICON } from '../utils/inversionUtils';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE_POSITIVO } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface Props { onClose: () => void; traspasoEditar?: TraspasoRecurrente; }

const PERIODICIDADES = [
  { v: 'diario',   l: 'Diario'   },
  { v: 'semanal',  l: 'Semanal'  },
  { v: 'mensual',  l: 'Mensual'  },
  { v: 'anual',    l: 'Anual'    },
] as const;

export const ModalTraspasoRecurrente: React.FC<Props> = ({ onClose, traspasoEditar }) => {
  useCerrarConEscape(onClose);
  const { crearTraspasoRecurrente, actualizarTraspasoRecurrente, cuentas, categorias, presupuestos, inversiones } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const modoEdicion = !!traspasoEditar;

  // ── Estado inicial detectando si el traspaso existente va a una inversión ──
  const initDestinoTipo = traspasoEditar?.inversionDestinoId ? 'inversion' : 'cuenta';

  const [nombre,       setNombre]   = useState(traspasoEditar?.nombre || '');
  const [cuentaOrigenId, setOrigen] = useState(traspasoEditar?.cuentaOrigenId || '');
  const [destinoTipo, setDestinoTipo] = useState<'cuenta' | 'inversion'>(initDestinoTipo);
  const [cuentaDestinoId, setDestino] = useState(traspasoEditar?.cuentaDestinoId || '');
  const [inversionDestinoId, setInvDest] = useState(traspasoEditar?.inversionDestinoId || '');
  const [cantidad,     setCantidad] = useState(traspasoEditar?.cantidad.toString() || '');
  const [descripcion,  setDescripcion] = useState(traspasoEditar?.descripcion || '');
  const [periodicidad, setPeriod]   = useState<'diario'|'semanal'|'mensual'|'anual'>(traspasoEditar?.periodicidad || 'mensual');
  const [proximaFecha, setProxima]  = useState(
    traspasoEditar ? new Date(traspasoEditar.proximaFecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [categoriaOrigenId, setCategoria]       = useState(traspasoEditar?.categoriaOrigenId || '');
  const [presupuestoOrigenId, setPresup]        = useState(traspasoEditar?.presupuestoOrigenId || '');
  const [presupuestoDestinoId, setPresupDest]   = useState(traspasoEditar?.presupuestoDestinoId || '');
  const [tieneFechaFin, setTieneFechaFin] = useState(!!traspasoEditar?.fechaFin);
  const [fechaFin, setFechaFin]     = useState(
    traspasoEditar?.fechaFin ? new Date(traspasoEditar.fechaFin).toISOString().split('T')[0] : ''
  );
  const [loading, setLoading]       = useState(false);
  const [error,   setError]         = useState('');

  const inputClass = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass = "block text-gray-700 dark:text-gray-300 font-semibold mb-2";

  const categoriasGasto = categorias.filter(c => c.tipo === 'gasto');
  const inversionesActivas = inversiones.filter(i => i.activo);

  // Presupuestos de ahorro anuales para el año de la próxima fecha
  const presupuestosAhorro = useMemo(() => {
    const anioFecha = new Date(proximaFecha).getFullYear().toString();
    return presupuestos.filter(
      p => p.esObjetivoAhorro === true && p.periodicidad === 'anual' && p.mes === anioFecha
    );
  }, [presupuestos, proximaFecha]);

  const presupuestosDisponibles = useMemo(() => {
    if (!categoriaOrigenId) return [];
    const fechaRef = new Date(proximaFecha);
    const mesTx   = `${fechaRef.getFullYear()}-${String(fechaRef.getMonth() + 1).padStart(2, '0')}`;
    const anioTx  = String(fechaRef.getFullYear());
    return presupuestos.filter(p => {
      if (p.categoriaId !== categoriaOrigenId) return false;
      if (modoEdicion) return true;
      if (p.periodicidad === 'mensual') return p.mes === mesTx;
      if (p.periodicidad === 'anual')   return p.mes === anioTx;
      return false;
    });
  }, [categoriaOrigenId, presupuestos, proximaFecha, modoEdicion]);

  const destinoEsHucha = destinoTipo === 'cuenta' && !!cuentaDestinoId &&
    cuentas.find(c => c.id === cuentaDestinoId)?.tipo === 'hucha';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre || !cuentaOrigenId || !cantidad) {
      setError('Completa todos los campos obligatorios'); return;
    }
    if (destinoTipo === 'cuenta') {
      if (!cuentaDestinoId) { setError('Selecciona la cuenta destino'); return; }
      if (cuentaOrigenId === cuentaDestinoId) { setError('Las cuentas origen y destino no pueden ser la misma'); return; }
    } else {
      if (!inversionDestinoId) { setError('Selecciona el producto de inversión destino'); return; }
    }
    setLoading(true);
    try {
      const datos: Omit<TraspasoRecurrente, 'id' | 'usuarioId' | 'createdAt'> = {
        nombre, cuentaOrigenId,
        ...(destinoTipo === 'cuenta'    ? { cuentaDestinoId    } : {}),
        ...(destinoTipo === 'inversion' ? { inversionDestinoId } : {}),
        cantidad: aNumero(cantidad), descripcion, periodicidad,
        proximaFecha: new Date(proximaFecha),
        activo: true,
        ...(tieneFechaFin && fechaFin ? { fechaFin: new Date(fechaFin) } : {}),
        ...(destinoTipo === 'cuenta' && categoriaOrigenId   ? { categoriaOrigenId   } : {}),
        ...(destinoTipo === 'cuenta' && presupuestoOrigenId ? { presupuestoOrigenId } : {}),
        ...(destinoTipo === 'inversion' && presupuestoDestinoId ? { presupuestoDestinoId } : {}),
      };
      if (modoEdicion && traspasoEditar) {
        await actualizarTraspasoRecurrente(traspasoEditar.id, datos);
      } else {
        await crearTraspasoRecurrente(datos);
      }
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {modoEdicion ? 'Editar Traspaso Periódico' : 'Nuevo Traspaso Periódico'}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

          <div>
            <label className={labelClass}>Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputClass} placeholder="Ej: Aportación mensual ETF" required />
          </div>

          <div>
            <label className={labelClass}>Importe (€)</label>
            <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE_POSITIVO} title={AVISO_IMPORTE} value={cantidad}
              onChange={e => setCantidad(e.target.value)}
              className={inputClass} placeholder="0,00" required />
          </div>

          <div>
            <label className={labelClass}>Cuenta Origen</label>
            <select value={cuentaOrigenId}
              onChange={e => { setOrigen(e.target.value); if (cuentaDestinoId === e.target.value) setDestino(''); }}
              className={inputClass} required>
              <option value="">Selecciona cuenta origen</option>
              {cuentas.map(c => (
                <option key={c.id} value={c.id}>{c.nombre} — €{c.saldo.toFixed(2)}</option>
              ))}
            </select>
          </div>

          {/* Toggle destino */}
          <div>
            <label className={labelClass}>Destino</label>
            <div className="flex gap-2 mb-3">
              <button type="button"
                onClick={() => { setDestinoTipo('cuenta'); setInvDest(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm transition ${
                  destinoTipo === 'cuenta'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>
                <ArrowRightLeft className="w-3.5 h-3.5" /> Cuenta bancaria
              </button>
              <button type="button"
                onClick={() => { setDestinoTipo('inversion'); setDestino(''); setCategoria(''); setPresup(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-semibold text-sm transition ${
                  destinoTipo === 'inversion'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>
                <TrendingUp className="w-3.5 h-3.5" /> Inversión
              </button>
            </div>

            {/* Cuenta destino */}
            {destinoTipo === 'cuenta' && (
              <select value={cuentaDestinoId}
                onChange={e => { setDestino(e.target.value); setCategoria(''); setPresup(''); }}
                className={inputClass} required>
                <option value="">Selecciona cuenta destino</option>
                {cuentas.filter(c => c.id !== cuentaOrigenId).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.tipo === 'hucha' ? '🐷 ' : ''}{c.nombre}
                  </option>
                ))}
              </select>
            )}

            {/* Inversión destino */}
            {destinoTipo === 'inversion' && (
              <select value={inversionDestinoId}
                onChange={e => { setInvDest(e.target.value); setPresupDest(''); }}
                className={inputClass} required>
                <option value="">Selecciona el producto de inversión</option>
                {inversionesActivas.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {TIPO_INVERSION_ICON[inv.tipo]} {inv.nombre} — {inv.broker}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Presupuesto de ahorro — solo cuando destino es inversión */}
          {destinoTipo === 'inversion' && inversionDestinoId && (
            <div>
              <label className={labelClass}>
                <span className="flex items-center gap-1.5">
                  <PiggyBank className="w-4 h-4 text-purple-500" />
                  Objetivo de ahorro asociado
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
                </span>
              </label>
              {presupuestosAhorro.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
                  No hay objetivos de ahorro anuales para {new Date(proximaFecha).getFullYear()}.
                </p>
              ) : (
                <select value={presupuestoDestinoId} onChange={e => setPresupDest(e.target.value)}
                  className={inputClass}>
                  <option value="">Sin objetivo asociado (inversión libre)</option>
                  {presupuestosAhorro.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} — objetivo €{p.limite.toFixed(2)}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                {presupuestoDestinoId
                  ? 'Cada ejecución contabilizará como ahorro hacia el objetivo seleccionado.'
                  : 'Sin asociar, solo afecta a tu posición financiera.'}
              </p>
            </div>
          )}

          {/* Categoría y presupuesto — solo para traspasos a cuentas */}
          {destinoTipo === 'cuenta' && (
            <>
              <div>
                <label className={labelClass}>
                  Categoría del gasto
                  {destinoEsHucha && <span className="ml-1 text-amber-600 dark:text-amber-400 text-xs font-normal">recomendado para hucha</span>}
                  {!destinoEsHucha && <span className="ml-1 text-gray-500 dark:text-gray-400 text-xs font-normal">(opcional)</span>}
                </label>
                <select value={categoriaOrigenId}
                  onChange={e => { setCategoria(e.target.value); setPresup(''); }}
                  className={inputClass}>
                  <option value="">Sin categoría</option>
                  {categoriasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.icono} {c.nombre}</option>
                  ))}
                </select>
              </div>

              {presupuestosDisponibles.length > 0 && (
                <div>
                  <label className={labelClass}>
                    Imputar a presupuesto
                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
                  </label>
                  <select value={presupuestoOrigenId} onChange={e => setPresup(e.target.value)}
                    className={inputClass}>
                    <option value="">Sin presupuesto específico</option>
                    {presupuestosDisponibles.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre} — límite €{p.limite.toFixed(2)}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div>
            <label className={labelClass}>Periodicidad</label>
            <div className="grid grid-cols-4 gap-2">
              {PERIODICIDADES.map(({ v, l }) => (
                <button key={v} type="button" onClick={() => setPeriod(v)}
                  className={`py-2 rounded-lg text-sm font-semibold transition ${
                    periodicidad === v
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>Primera ejecución</label>
            <input type="date" value={proximaFecha} onChange={e => setProxima(e.target.value)}
              className={inputClass} required />
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer mb-2">
              <input type="checkbox" checked={tieneFechaFin}
                onChange={e => { setTieneFechaFin(e.target.checked); if (!e.target.checked) setFechaFin(''); }}
                className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-gray-700 dark:text-gray-300 font-semibold">Fecha de fin</span>
            </label>
            {tieneFechaFin && (
              <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                className={inputClass} required={tieneFechaFin} />
            )}
            {!tieneFechaFin && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Sin fecha fin — se repite indefinidamente</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Descripción (opcional)</label>
            <input type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
              className={inputClass} placeholder="Motivo" />
          </div>

          <div className="flex gap-4 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
              {loading ? 'Guardando…' : modoEdicion ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
